/**
 * On-time streak reward card (NAU-247 / NAU-268).
 *
 * Matches the reference treatment: a white card with a thick bordered-circle
 * trophy. Tier thresholds (streak.ts) and the day count are both real — the
 * count comes from the dashboard streak endpoint.
 */

import type { LucideIcon } from "lucide-react";
import { AlertTriangle, Minus, Trophy } from "lucide-react";

import { getStreakTier, type StreakTier } from "@/lib/dashboard/streak";

import { OverviewCard } from "./_shared";
import { NAUD, type NaudTone } from "./NaudIcon";

const TONE_MAP: Record<
  StreakTier["tone"],
  { naud: NaudTone; icon: LucideIcon }
> = {
  gold: { naud: "orange", icon: Trophy },
  purple: { naud: "purple", icon: Trophy },
  blue: { naud: "blue", icon: Trophy },
  red: { naud: "red", icon: AlertTriangle },
  grey: { naud: "navy", icon: Minus },
};

export function OnTimeStreakCard({
  days,
  graceHours,
}: {
  days: number | null;
  /**
   * The grace window the server actually enforced. Rendered rather than
   * hardcoded so the card cannot drift from the rule (NAU-394).
   */
  graceHours?: number;
}) {
  const tier = getStreakTier(days);
  const { naud, icon: Icon } = TONE_MAP[tier.tone];
  const color = NAUD[naud].solid;

  return (
    <OverviewCard className="w-full p-5">
      <div className="flex items-center justify-between">
        <span className="text-caption-bold font-caption-bold text-subtext-color">
          On-time streak
        </span>
      </div>
      <div className="mt-3 flex items-center gap-5">
        <div
          className="grid h-20 w-20 shrink-0 place-items-center rounded-full border-[7px] border-solid"
          style={{ borderColor: color }}
        >
          <Icon size={36} strokeWidth={2.4} color={color} />
        </div>
        <div className="flex flex-col">
          <span className="text-heading-1 font-heading-1 leading-none text-neutral-900">
            {days === null ? "—" : `${days} day${days === 1 ? "" : "s"}`}
          </span>
          <span className="mt-2 text-caption font-caption text-subtext-color">
            {tier.message}
          </span>
        </div>
      </div>
      <p className="mt-4 text-caption font-caption text-subtext-color">
        {graceHours
          ? `Checks completed within ${graceHours} hours of their due time still count. Reporting a problem never breaks your streak.`
          : "Reporting a problem never breaks your streak."}
      </p>
    </OverviewCard>
  );
}
